import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useHistory} from 'react-router-dom';
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TableSortLabel,
	Checkbox,
	IconButton,
	Chip,
	Avatar,
	Box,
	Typography,
	Tooltip,
	CircularProgress,
	LinearProgress,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Button,
} from '@material-ui/core';
import {
	MoreVert,
	Visibility,
	Edit,
	LocalShipping,
	Receipt,
	AccountBalanceWallet,
	History,
	Email,
	Refresh,
	Schedule,
} from '@material-ui/icons';
import {useOrders, canServerSortRemaining} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';
import moment from 'moment';
import delishopLogo from '../../../../assets/delishop_round_logo.png';
import delixpressLogo from '../../../../assets/delixpress_round_logo.png';
import wingLogo from '../../../../assets/wing_logo.png';

const useStyles = makeStyles((theme) => ({
	// Basket-type logo plus, for a Wing order, the channel mark under it.
	// Stacked rather than side by side so the column stays narrow and every row
	// lines up on the same centre axis whether or not a channel mark is present.
	orderTypeCell: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 3,
	},
	// The basket logos are square app icons, so height is pinned alongside width:
	// the cell then reserves the same box before the image loads and rows do not
	// jump as the table paints.
	orderTypeLogo: {
		display: 'block',
		width: 32,
		height: 32,
	},
	// Wing's app icon is a full square — the rounded-corner look is applied here,
	// the same way a phone home screen does it, rather than by editing the art.
	channelMark: {
		display: 'block',
		width: 18,
		height: 18,
		borderRadius: 4,
	},
	tablePaper: {
		position: 'relative',
		borderRadius: 12,
		boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
		overflow: 'visible',
	},
	tableContainer: {
		maxHeight: 'calc(100vh - 450px)',
		minHeight: 400,
	},
	tableHead: {
		backgroundColor: '#f8f9fa',
		'& .MuiTableCell-head': {
			fontWeight: 700,
			color: theme.palette.text.primary,
			fontSize: 13,
			textTransform: 'uppercase',
			letterSpacing: 0.5,
			borderBottom: `2px solid ${theme.palette.divider}`,
		},
	},
	tableRow: {
		'&:hover': {
			backgroundColor: '#f8f9fa',
		},
		transition: 'background-color 0.2s ease',
	},
	selectedRow: {
		backgroundColor: '#e3f2fd !important',
	},
	statusChip: {
		fontWeight: 600,
		fontSize: 12,
		minWidth: 100,
	},
	orderIdCell: {
		fontWeight: 700,
		color: theme.palette.primary.main,
		fontFamily: 'monospace',
	},
	customerInfo: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
	},
	avatar: {
		width: 36,
		height: 36,
		backgroundColor: theme.palette.primary.main,
		fontSize: 14,
		fontWeight: 700,
	},
	customerName: {
		fontWeight: 600,
		fontSize: 14,
		lineHeight: 1.2,
		marginBottom: 4,
	},
	customerEmail: {
		fontSize: 12,
		color: theme.palette.text.secondary,
	},
	amountCell: {
		fontWeight: 700,
		fontSize: 15,
		fontFamily: 'monospace',
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: theme.spacing(8),
	},
	emptyContainer: {
		textAlign: 'center',
		padding: theme.spacing(8),
		color: theme.palette.text.secondary,
	},
	actionButton: {
		padding: 6,
	},
}));

// Status configurations
const statusConfig = {
	'-1': {label: 'Waiting Payment', color: '#ff5722', bgColor: '#fbe9e7'},
	'0': {label: 'Order Saved', color: '#9e9e9e', bgColor: '#f5f5f5'},
	'1': {label: 'Order Removed', color: '#757575', bgColor: '#eeeeee'},
	'2': {label: 'New', color: '#ff9800', bgColor: '#fff3e0'},
	'3': {label: 'Accepted', color: '#2196f3', bgColor: '#e3f2fd'},
	'4': {label: 'Packed', color: '#9c27b0', bgColor: '#f3e5f5'},
	'5': {label: 'On The Way', color: '#3f51b5', bgColor: '#e8eaf6'},
	'6': {label: 'Delivered', color: '#4caf50', bgColor: '#e8f5e9'},
	'7': {label: 'Cancelled', color: '#f44336', bgColor: '#ffebee'},
	'8': {label: 'Arrived', color: '#00bcd4', bgColor: '#e0f7fa'},
};

// order_details.source for an order placed inside the Wing Mini App. Written
// server-side from a verified channel token (api/miniapp), never from the
// client, so it is safe to read as the truth about where an order came from.
const WING_SOURCE = 'wing';

const isWingOrder = (order) =>
	String(order && order.source ? order.source : '').toLowerCase() === WING_SOURCE;

// One sentence for the Type column's tooltip, naming both facts. Older orders
// predate the source column and simply read as Delishop.
const orderChannelLabel = (order) => {
	const basket = order && order.type === 1 ? 'Express' : 'Normal';
	return isWingOrder(order)
		? `${basket} order — placed in the Wing Mini App`
		: `${basket} order — placed on Delishop`;
};

const OrdersTable = () => {
	const classes = useStyles();
	const history = useHistory();
	const {state, actions} = useOrders();
	const alert = useAlert();
	const {
		orders,
		loading,
		page,
		rowsPerPage,
		totalCount,
		selectedOrderIds,
		orderBy,
		order,
		statusFilter,
		dateRange,
	} = state;

	const [anchorEl, setAnchorEl] = React.useState(null);
	const [selectedOrder, setSelectedOrder] = React.useState(null);
	const [queryLoading, setQueryLoading] = React.useState({});
	const [queryDisable, setQueryDisable] = React.useState(false);

	// Remaining is sorted client-side when the range is unbounded ("All") or too
	// wide for the expensive server-side pricing. null = no client sort.
	const [clientRemainingSort, setClientRemainingSort] = React.useState(null);

	// Check if we're in waiting payment mode (query mode)
	const isWaitingPayment = statusFilter === '-1';

	// Handle menu
	const handleMenuOpen = (event, order) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setSelectedOrder(order);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setSelectedOrder(null);
	};

	// Handle row selection
	const handleSelectAll = (event) => {
		if (event.target.checked) {
			actions.selectAllOrders();
		} else {
			actions.clearSelection();
		}
	};

	const handleRowSelect = (event, orderId) => {
		event.stopPropagation();
		actions.toggleOrderSelection(orderId);
	};

	const isSelected = (orderId) => selectedOrderIds.includes(orderId);
	const isAllSelected = orders.length > 0 && selectedOrderIds.length === orders.length;
	const isIndeterminate = selectedOrderIds.length > 0 && selectedOrderIds.length < orders.length;

	// Handle sorting. Remaining opens on DESC: the reason to sort by it is to see
	// who owes the most, so the first click should land on the largest balances.
	// On an unbounded or >~6-month range the backend pricing is too expensive, so
	// Remaining sorts only the rows currently loaded in the table.
	const handleSort = (field) => {
		if (field === 'remaining_amount') {
			if (canServerSortRemaining(dateRange)) {
				setClientRemainingSort(null);
				if (orderBy !== field) {
					actions.setSorting(field, 'DESC');
				} else {
					actions.setSorting(field, order === 'ASC' ? 'DESC' : 'ASC');
				}
				return;
			}
			setClientRemainingSort(
				clientRemainingSort === 'desc' ? 'asc' : (clientRemainingSort === 'asc' ? null : 'desc')
			);
			return;
		}
		setClientRemainingSort(null);
		if (orderBy !== field) {
			actions.setSorting(field, 'ASC');
			return;
		}
		actions.setSorting(field, order === 'ASC' ? 'DESC' : 'ASC');
	};

	// Rows sorted by remaining on the client (active only while the server sort is
	// not allowed, i.e. on "All" dates or a range wider than ~6 months).
	const displayedOrders = React.useMemo(() => {
		if (!clientRemainingSort) return orders;
		const dir = clientRemainingSort === 'asc' ? 1 : -1;
		return [...orders].sort((a, b) => {
			const ra = parseFloat(a.remaining_amount || 0);
			const rb = parseFloat(b.remaining_amount || 0);
			return (ra - rb) * dir;
		});
	}, [orders, clientRemainingSort]);

	// Handle pagination
	const handleChangePage = (event, newPage) => {
		actions.setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		actions.setRowsPerPage(parseInt(event.target.value, 10));
	};

	// Handle order ID click - open in new tab
	const handleOrderIdClick = (event, orderId) => {
		event.stopPropagation();
		window.open(`/admin/order/details/${orderId}`, '_blank');
	};

	// Handle customer click - open in new tab
	const handleCustomerClick = (event, customerName) => {
		event.stopPropagation();
		if (customerName) {
			window.open(`/admin/customers?search=${encodeURIComponent(customerName)}`, '_blank');
		}
	};

	// Handle Query button for waiting payment
	const handleQueryOrder = async (event, orderId) => {
		event.stopPropagation();
		setQueryLoading(prev => ({...prev, [orderId]: true}));
		setQueryDisable(true);

		try {
			const response = await API.queryOrder({order_id: orderId})
				.then(res => res.json());

			if (response && response.success) {
				// Show payment details using SweetAlert
				const data = response.data;
				const paymentMethod = data?.meta?.setting?.enabled_payment_methods?.[0]?.replace('_', ' ') || 'N/A';
				const amount = data?.total_amount || 0;
				const status = data?.status || 'N/A';
				const outTradeNo = data?.out_trade_no || 'N/A';

				const paymentDetails =
					`<div style="text-align: left;">
            <p><strong>Payment Method:</strong> ${paymentMethod}</p>
            <p><strong>Amount:</strong> $${amount.toFixed(2)}</p>
            <p><strong>Status:</strong> ${status}</p>
            <p><strong>Out Trade No:</strong> ${outTradeNo}</p>
            <p><strong>Created:</strong> ${moment(data?.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
          </div>`;

				alert.fire({
					title: 'Payment Details',
					html: paymentDetails,
					icon: 'info',
					confirmButtonText: 'OK',
				});
			} else {
				alert.error('Error', response?.message || 'Failed to query order');
			}
		} catch (error) {
			console.error('Query order error:', error);
			alert.error('Error', 'Error querying order: ' + error.message);
		} finally {
			setQueryLoading(prev => ({...prev, [orderId]: false}));
			setQueryDisable(false);
		}
	};

	// Handle Update Out Trade No
	const handleUpdateOutTradeNo = async (event, orderId) => {
		event.stopPropagation();

		const result = await alert.confirm(
			'Update Out Trade No',
			`You are about to update out trade no of order #${orderId}!`,
			'Update',
			'Cancel'
		);

		if (!result.isConfirmed) {
			return;
		}

		try {
			const response = await API.updateOutTradeNo(orderId)
				.then(res => res.json());

			if (response && response.success) {
				alert.success('Success', 'Out Trade No updated successfully');
				setTimeout(() => {
					actions.refreshData();
				}, 1000);
			} else {
				alert.error('Error', response?.message || 'Failed to update Out Trade No');
			}
		} catch (error) {
			console.error('Update Out Trade No error:', error);
			alert.error('Error', 'Error updating Out Trade No: ' + error.message);
		}
	};

	// Menu actions
	const handleViewDetails = () => {
		window.open(`/admin/order/details/${selectedOrder.order_id}`, '_blank');
		handleMenuClose();
	};

	const handleAssignDriver = () => {
		actions.openDriverDialog(selectedOrder);
		handleMenuClose();
	};

	const handleGenerateInvoice = async () => {
		try {
			await API.generateInvoice({
				order_id: selectedOrder.order_id,
			});
			alert.success('Success', 'Generating Invoice and Sending');
			handleMenuClose();
		} catch (error) {
			console.error('Generate invoice error:', error);
			alert.error('Error', 'Failed to generate invoice');
			handleMenuClose();
		}
	};

	const handleSendEmail = async () => {
		try {
			await API.generateInvoice({
				order_id: selectedOrder.order_id,
			});
			alert.success('Success', 'Email sent successfully');
			handleMenuClose();
		} catch (error) {
			console.error('Send email error:', error);
			alert.error('Error', 'Failed to send email');
			handleMenuClose();
		}
	};

	const handleViewWallet = () => {
		actions.openWalletDialog(selectedOrder);
		handleMenuClose();
	};

	const handleViewHistory = () => {
		actions.openHistoryDialog(selectedOrder);
		handleMenuClose();
	};

	const handleUpdateSlot = () => {
		actions.openUpdateSlotDialog(selectedOrder, selectedOrder.slot_id);
		handleMenuClose();
	};

	const handleUpdateDeliveredAt = () => {
		actions.openDeliveredAtDialog(selectedOrder, selectedOrder.delivered_at, selectedOrder.slot_id);
		handleMenuClose();
	};

	// Handle Accept Order (status 2 -> 3)
	const handleAcceptOrder = async (event, orderId) => {
		event.stopPropagation();

		try {
			const response = await API.changeOrderStatus({
				order_id: orderId,
				order_status: 3,
				store_id: 1,
			}).then(res => res.json());

			if (response.success) {
				alert.success('Success', 'Order accepted successfully');
				actions.refreshData();
			} else {
				alert.error('Error', response.message || 'Failed to accept order');
			}
		} catch (error) {
			console.error('Accept order error:', error);
			alert.error('Error', 'Error accepting order: ' + error.message);
		}
	};

	// Handle Cancel Order (status 2 or 3 -> 7)
	const handleCancelOrder = async (event, orderId) => {
		event.stopPropagation();

		const result = await alert.confirm(
			'Cancel Order',
			`Are you sure you want to cancel order #${orderId}?`,
			'Yes, Cancel Order',
			'No'
		);

		if (!result.isConfirmed) {
			return;
		}

		try {
			const response = await API.changeOrderStatus({
				order_id: orderId,
				order_status: 7,
				store_id: 1,
			}).then(res => res.json());

			if (response.success) {
				alert.success('Success', 'Order cancelled successfully');
				actions.refreshData();
			} else {
				alert.error('Error', response.message || 'Failed to cancel order');
			}
		} catch (error) {
			console.error('Cancel order error:', error);
			alert.error('Error', 'Error cancelling order: ' + error.message);
		}
	};

	// Get customer initials
	const getInitials = (name) => {
		if (!name) return '?';
		const parts = name.trim().split(' ');
		if (parts.length >= 2) {
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}
		return name.substring(0, 2).toUpperCase();
	};

	// Get slot information based on slot_type
	const getSlotDisplay = (order) => {
		// Check if order type is 1 (ASAP)
		if (order.type === 1) {
			return 'ASAP';
		}

		// Get slot object
		const slot = order.store_on_going_slot ||
			(order.store_on_going_slots && order.store_on_going_slots.length > 0
				? order.store_on_going_slots[0]
				: null);

		if (!slot) {
			return 'Not scheduled';
		}

		const slotType = order.slot_type;

		// Hourly slot: show start_time - end_time format (same as medium and large)
		if (slotType === 'hourly') {
			const startTime = moment(slot.start_time).format('DD/MM/YYYY - h:mm A');
			const endTime = moment(slot.end_time).format('h:mm A');
			return `${startTime} - ${endTime}`;
		}

		// Medium slot: show date range with start and end time
		if (slotType === 'medium') {
			const startTime = moment(slot.start_time).format('DD/MM/YYYY - h:mm A');
			const endTime = moment(slot.end_time).format('h:mm A');
			return `${startTime} - ${endTime}`;
		}

		// Large slot: show same format as medium
		if (slotType === 'large') {
			const startTime = moment(slot.start_time).format('DD/MM/YYYY - h:mm A');
			const endTime = moment(slot.end_time).format('h:mm A');
			return `${startTime} - ${endTime}`;
		}

		// Fallback: show start_time if available
		if (slot.start_time) {
			return moment(slot.start_time).format('DD/MM/YYYY - h:mm A');
		}

		return 'Not scheduled';
	};

	// Column definitions - IDs must match API field names for sorting
	const baseColumns = [
		{id: 'select', label: '', width: 50, sortable: false},
		{id: 'type', label: 'Type', width: 86, sortable: false},
		{id: 'order_id', label: 'Order ID', width: 120, sortable: true},
		{id: 'customer_name', label: 'Customer', width: 200, sortable: true},
		{id: 'city', label: 'City', width: 120, sortable: false},
		{id: 'status', label: 'Status', width: 140, sortable: true},
		{id: 'amount', label: 'Amount', width: 100, sortable: true},
		{id: 'remaining_amount', label: 'Remaining', width: 100, sortable: true},
		{id: 'payment_method_name', label: 'Payment Method', width: 180, sortable: true},
		{id: 'createdAt', label: 'Order Date', width: 160, sortable: false},
		{id: 'start_time', label: 'Delivery Slot', width: 180, sortable: true},
	];

	// Add conditional columns for waiting payment
	const waitingPaymentColumns = [
		{id: 'out_trade_no', label: 'Out Trade No', width: 180, sortable: false},
		{id: 'query', label: 'Query', width: 120, sortable: false},
	];

	// Add action columns (Accept/Cancel) - show for all statuses except waiting payment
	const showActionColumns = statusFilter !== '-1';
	const actionColumns = showActionColumns ? [
		{id: 'accept', label: 'Accept', width: 100, sortable: false},
		{id: 'cancel', label: 'Cancel', width: 100, sortable: false},
	] : [];

	const columns = isWaitingPayment
		? [...baseColumns, ...waitingPaymentColumns, {id: 'actions', label: 'Actions', width: 80, sortable: false}]
		: [...baseColumns, ...actionColumns, {id: 'actions', label: 'Actions', width: 80, sortable: false}];

	if (loading && orders.length === 0) {
		return (
			<Paper className={classes.tablePaper} elevation={0}>
				<Box className={classes.loadingContainer}>
					<CircularProgress/>
				</Box>
			</Paper>
		);
	}

	if (!loading && orders.length === 0) {
		return (
			<Paper className={classes.tablePaper} elevation={0}>
				<Box className={classes.emptyContainer}>
					<Typography variant="h6" gutterBottom>
						No orders found
					</Typography>
					<Typography variant="body2">
						Try adjusting your filters or date range
					</Typography>
				</Box>
			</Paper>
		);
	}

	return (
		<>
			<Paper className={classes.tablePaper} elevation={0}>
				{/* Loading progress bar */}
				{loading && (
					<LinearProgress
						color="primary"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							zIndex: 1
						}}
					/>
				)}
				<TableContainer
					className={classes.tableContainer}
					style={{opacity: loading ? 0.6 : 1, transition: 'opacity 0.3s'}}
				>
					<Table>
						<TableHead className={classes.tableHead}>
							<TableRow>
								<TableCell padding="checkbox">
									<Checkbox
										indeterminate={isIndeterminate}
										checked={isAllSelected}
										onChange={handleSelectAll}
										color="primary"
									/>
								</TableCell>

								{columns.slice(1).map((column) => (
									<TableCell
										key={column.id}
										style={{width: column.width}}
									>
										{column.sortable ? (
											<TableSortLabel
												active={column.id === 'remaining_amount'
													? (canServerSortRemaining(dateRange) ? orderBy === column.id : !!clientRemainingSort)
													: orderBy === column.id}
												direction={column.id === 'remaining_amount' && !canServerSortRemaining(dateRange)
													? (clientRemainingSort || 'asc')
													: (orderBy === column.id ? order.toLowerCase() : 'asc')}
												onClick={() => handleSort(column.id)}
											>
												{column.label}
											</TableSortLabel>
										) : (
											column.label
										)}
									</TableCell>
								))}
							</TableRow>
						</TableHead>

						<TableBody>
							{displayedOrders.map((order) => {
								const selected = isSelected(order.order_id);
								const statusKey = String(order.status);
								const statusInfo = statusConfig[statusKey] || {label: 'Unknown', color: '#757575', bgColor: '#f5f5f5'};
								// Under the Late Orders filter every delivered row was handed over past its
								// timeslot, so flag it red even when the API doesn't set is_late on it.
								const isLate = order.is_late || (statusFilter === 'late-orders' && order.status === 6);

								return (
									<TableRow
										key={order.order_id}
										hover
										className={`${classes.tableRow} ${selected ? classes.selectedRow : ''}`}
										selected={selected}
									>
										<TableCell padding="checkbox">
											<Checkbox
												checked={selected}
												onChange={(e) => handleRowSelect(e, order.order_id)}
												color="primary"
											/>
										</TableCell>

										{/* Basket type (Normal / Express) + channel (Wing) */}
										{/*
										  Two independent facts, so they are shown as two marks
										  rather than one of three icons: `type` is the basket
										  (0 normal / 1 express) and `source` is the channel the
										  order was placed through. A Wing EXPRESS order is a real
										  combination, and collapsing them would hide it.
										*/}
										<TableCell align="center">
											<Tooltip title={orderChannelLabel(order)}>
												<Box className={classes.orderTypeCell}>
													<img
														src={order.type === 1 ? delixpressLogo : delishopLogo}
														alt={order.type === 1 ? 'Express' : 'Normal'}
														className={classes.orderTypeLogo}
													/>
													{isWingOrder(order) && (
														<img
															src={wingLogo}
															alt="Wing Mini App"
															className={classes.channelMark}
														/>
													)}
												</Box>
											</Tooltip>
										</TableCell>

										{/* Order ID - Link that's easy to copy */}
										<TableCell className={classes.orderIdCell}>
											<a
												href={`/admin/order/details/${order.order_id}`}
												target="_blank"
												rel="noopener noreferrer"
												style={{
													color: '#9cc54b',
													textDecoration: 'none',
													fontFamily: 'monospace',
													fontWeight: 700,
													userSelect: 'text'
												}}
												onMouseDown={(e) => {
													// Allow text selection - don't navigate on mouse down
													if (window.getSelection().toString()) {
														e.preventDefault();
													}
												}}
											>
												#{order.order_id}
											</a>
										</TableCell>

										{/* Customer Info - Clickable */}
										<TableCell
											onClick={(e) => handleCustomerClick(e, order.customer_detail?.name)}
											style={{cursor: 'pointer'}}
										>
											<Box className={classes.customerInfo}>
												<Avatar className={classes.avatar}>
													{getInitials(order.customer_detail?.name)}
												</Avatar>
												<Box>
													<Typography className={classes.customerName}>
														{order.customer_detail?.name || 'N/A'}
													</Typography>
													<Typography className={classes.customerEmail}>
														{order.customer_detail?.email || order.customer_detail?.phone || 'No contact'}
													</Typography>
												</Box>
											</Box>
										</TableCell>

										{/* City */}
										<TableCell>
											<Typography variant="body2">
												{order.list_customer_address?.city || 'N/A'}
											</Typography>
										</TableCell>

										{/* Status */}
										<TableCell>
											<Chip
												label={statusInfo.label}
												className={classes.statusChip}
												size="small"
												style={{
													backgroundColor: isLate ? '#ffebee' : statusInfo.bgColor,
													color: isLate ? '#f44336' : statusInfo.color,
													fontWeight: isLate ? 700 : 600,
												}}
											/>
										</TableCell>

										{/* Amount */}
										<TableCell className={classes.amountCell}>
											${parseFloat(order.order_total || 0).toFixed(2)}
										</TableCell>

										{/* Remaining Amount */}
										<TableCell className={classes.amountCell}>
											{(() => {
												const remaining = parseFloat(order.remaining_amount || 0);
												const color = remaining < 0 ? '#d32f2f' : '#2e7d32';
												return (
													<span style={{color, fontWeight: 500}}>
														{Math.abs(remaining) < 0.01
															? '0.00'
															: remaining < 0
																? `-$${Math.abs(remaining).toFixed(2)}`
																: `$${remaining.toFixed(2)}`}
													</span>
												);
											})()}
										</TableCell>

										{/* Payment Method */}
										<TableCell>
											<Typography variant="body2">
												{(() => {
													const payment = order.order_payment_detail || (order.order_payment_details && order.order_payment_details[0]);

													// Check for ABA payment with failed/ongoing status
													if (order.payment_method_name === "Paid online (ABA)" && payment?.p_status) {
														return "Paid online (ABA) Payment failed/Ongoing";
													}

													// Check for wallet payment
													if (payment?.amount === 0) {
														return "Paid by wallet";
													}

													// Default to payment method name
													return order.payment_method_name || 'N/A';
												})()}
											</Typography>
										</TableCell>

										{/* Order Date */}
										<TableCell>
											<Typography variant="body2">
												{moment(order.placedAt).format('MMM DD, YYYY')}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{moment(order.placedAt).format('hh:mm A')}
											</Typography>
										</TableCell>

										{/* Delivery Slot */}
										<TableCell>
											<Typography variant="body2">
												{getSlotDisplay(order)}
											</Typography>
										</TableCell>

										{/* Out Trade No - Only for waiting payment */}
										{isWaitingPayment && (
											<TableCell>
												<Box display="flex" alignItems="center" gap={1}>
													<Typography variant="body2">
														{order.order_payment_detail?.p_txnld || '-'}
													</Typography>
													<Tooltip title={`Update out trade no - #${order.order_id}`}>
														<IconButton
															size="small"
															onClick={(e) => handleUpdateOutTradeNo(e, order.order_id)}
															color="primary"
														>
															<Refresh fontSize="small"/>
														</IconButton>
													</Tooltip>
												</Box>
											</TableCell>
										)}

										{/* Query Button - Only for waiting payment */}
										{isWaitingPayment && (
											<TableCell>
												<IconButton
													size="small"
													onClick={(e) => handleQueryOrder(e, order.order_id)}
													disabled={queryDisable}
													style={{
														backgroundColor: '#ff9800',
														color: '#fff',
														padding: '6px 12px',
														borderRadius: 4,
													}}
												>
													{queryLoading[order.order_id] ? (
														<CircularProgress size={16} style={{color: '#fff'}}/>
													) : (
														<Typography variant="caption" style={{fontWeight: 600}}>
															Query
														</Typography>
													)}
												</IconButton>
											</TableCell>
										)}

										{/* Accept Button - Show for status 2 (New) */}
										{showActionColumns && (
											<TableCell>
												{order.status === 2 && (
													<Button
														variant="contained"
														color="primary"
														size="small"
														onClick={(e) => handleAcceptOrder(e, order.order_id)}
														style={{
															backgroundColor: '#4caf50',
															textTransform: 'none',
															fontSize: '0.85rem',
															padding: '5px 12px',
															minWidth: '70px',
															height: '30px',
														}}
													>
														Accept
													</Button>
												)}
											</TableCell>
										)}

										{/* Cancel Button - Show for all statuses except -1 (waiting payment), 6 (delivered), and 7 (cancelled) */}
										{showActionColumns && (
											<TableCell>
												{order.status !== -1 && order.status !== 6 && order.status !== 7 && (
													<Button
														variant="contained"
														size="small"
														onClick={(e) => handleCancelOrder(e, order.order_id)}
														style={{
															backgroundColor: '#f44336',
															color: '#fff',
															textTransform: 'none',
															fontSize: '0.85rem',
															padding: '5px 12px',
															minWidth: '70px',
															height: '30px',
														}}
													>
														Cancel
													</Button>
												)}
											</TableCell>
										)}

										{/* Actions */}
										<TableCell>
											<IconButton
												size="small"
												onClick={(e) => handleMenuOpen(e, order)}
												className={classes.actionButton}
											>
												<MoreVert/>
											</IconButton>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					rowsPerPageOptions={[10, 25, 30, 50, 100]}
				/>
			</Paper>

			{/* Actions Menu */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				transformOrigin={{vertical: 'top', horizontal: 'right'}}
			>
				<MenuItem onClick={handleViewDetails}>
					<ListItemIcon>
						<Visibility fontSize="small"/>
					</ListItemIcon>
					<ListItemText primary="View Details"/>
				</MenuItem>

				<MenuItem onClick={handleAssignDriver}>
					<ListItemIcon>
						<LocalShipping fontSize="small"/>
					</ListItemIcon>
					<ListItemText primary="Assign Driver"/>
				</MenuItem>

				{/* Update Slot - Only for normal orders (type 0) with status 2 or 3 */}
				{selectedOrder && selectedOrder.type === 0 && (selectedOrder.status === 2 || selectedOrder.status === 3) && (
					<MenuItem onClick={handleUpdateSlot}>
						<ListItemIcon>
							<Schedule fontSize="small"/>
						</ListItemIcon>
						<ListItemText primary="Update Slot"/>
					</MenuItem>
				)}

				{/* Update Delivered At - Only for status >= 4 and not cancelled */}
				{selectedOrder && selectedOrder.status >= 4 && selectedOrder.status !== 7 && (
					<MenuItem onClick={handleUpdateDeliveredAt}>
						<ListItemIcon>
							<Schedule fontSize="small"/>
						</ListItemIcon>
						<ListItemText primary="Update Delivered At"/>
					</MenuItem>
				)}

				<MenuItem onClick={handleGenerateInvoice}>
					<ListItemIcon>
						<Receipt fontSize="small"/>
					</ListItemIcon>
					<ListItemText primary="Generate Invoice"/>
				</MenuItem>

				{/*/!* Send Email - Only for status 2 or 3 *!/*/}
				{/*{selectedOrder && (selectedOrder.status === 2 || selectedOrder.status === 3) && (*/}
				{/*	<MenuItem onClick={handleSendEmail}>*/}
				{/*		<ListItemIcon>*/}
				{/*			<Email fontSize="small"/>*/}
				{/*		</ListItemIcon>*/}
				{/*		<ListItemText primary="Send Email"/>*/}
				{/*	</MenuItem>*/}
				{/*)}*/}

				<MenuItem onClick={handleViewWallet}>
					<ListItemIcon>
						<AccountBalanceWallet fontSize="small"/>
					</ListItemIcon>
					<ListItemText primary="Wallet Transactions"/>
				</MenuItem>

				<MenuItem onClick={handleViewHistory}>
					<ListItemIcon>
						<History fontSize="small"/>
					</ListItemIcon>
					<ListItemText primary="Orders History"/>
				</MenuItem>
			</Menu>
		</>
	);
};

export default OrdersTable;
